import React from 'react';
import type { VendorProduct } from '../types';

interface Props {
  data: VendorProduct[] | null;
  canManageProducts: boolean;
  onAddProduct: () => void;
  onEditProduct: (product: VendorProduct) => void;
  onDeleteProduct: (productIdx: number) => void;
}

export default function VendorProductsTab({ data, canManageProducts, onAddProduct, onEditProduct, onDeleteProduct }: Props) {
  if (!data) return <div className="p-8 text-center text-gray-500">로딩중...</div>;

  return (
    <div className="space-y-6">
      {/* 상품 목록 헤더 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">상품 목록</h3>
            <p className="text-sm text-gray-600 mt-1">총 {data.length}개의 상품</p>
          </div>
          {canManageProducts && (
            <button
              onClick={onAddProduct}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              상품 등록
            </button>
          )}
        </div>
      </div>

      {/* 상품 테이블 */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">상품번호</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">상품명</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">카테고리</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">공급가</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">판매가</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">재고</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">상태</th>
                {canManageProducts && (
                  <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">관리</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={canManageProducts ? 8 : 7} className="px-6 py-10 text-center text-gray-500">등록된 상품이 없습니다.</td>
                </tr>
              ) : (
                data.map((product) => (
                  <tr key={product.productIdx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">
                      {product.productCode || `PRD-${product.productIdx}`}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{product.name}</td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">
                      {typeof product.costPrice === 'number' ? `${product.costPrice.toLocaleString()}원` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                      {product.salesPrice.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-center">
                      {product.stockQuantity !== null ? product.stockQuantity : '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.status === 'SALE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {product.status || '미정'}
                      </span>
                    </td>
                    {canManageProducts && (
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => onEditProduct(product)}
                            className="text-gray-600 hover:text-blue-600 w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded transition-colors cursor-pointer"
                            title="수정"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => onDeleteProduct(product.productIdx)}
                            className="text-gray-600 hover:text-red-600 w-8 h-8 flex items-center justify-center hover:bg-red-50 rounded transition-colors cursor-pointer"
                            title="삭제"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
